/** Copyright (c) 2018 T.Rowe Price. All rights reserved. */

import './DashboardPage.scss';

import { isEmpty } from 'lodash';
import { object } from 'prop-types';
import React, { Fragment } from 'react';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';

import { SectionHeader } from 'components/pages/Header/SectionHeader';
import AggregatePerformanceCard from 'components/layouts/Performance/AggregatePerformanceCard';
import AggregatePerformanceDataProvider from 'components/shared/DataProviders/AggregatePerformanceDataProvider';
import CurrentRequiredMinimumDistributionCard from 'components/layouts/RequiredMinimumDistribution/CurrentRequiredMinimumDistributionCard';
import RequiredMinimumDistributionCard from 'components/layouts/RequiredMinimumDistribution/RequiredMinimumDistributionCard';
import RequiredMinimumDistributionDataProvider from 'components/shared/DataProviders/RequiredMinimumDistributionDataProvider';
import ServiceBenefits from 'components/layouts/ServiceBenefits/ServiceBenefits';
import ServiceBenefitsDataProvider from 'components/shared/DataProviders/ServiceBenefitsDataProvider';
import PersonalizedServices from 'components/layouts/PersonalizedServices/PersonalizedServices';
import PromotionsCard from 'components/layouts/Promotions/PromotionsCard';
import CallToAction from 'components/layouts/CallToAction/CallToAction';
import AsOfDateModal from 'components/shared/AsOfDate/AsOfDateModal';
import Content, { contentConverter } from 'services/Content';
import ExternalInvestmentProductCard from 'components/layouts/ExternalInvestmentProductCard/NewExternalInvestmentProductCard';
import { irtMessageTypes } from 'state/IRT/IRTMessagesReducer';
import InsightCards from 'components/layouts/InsightsCards/InsightsCards';
import TestAndTarget from 'components/shared/TestAndTarget/TestAndTarget';
import UnfundedAccountCard from 'components/layouts/UnfundedAccountCard/UnfundedAccountCard';
import NASUSessionCard from 'components/layouts/NASUSessionCard/NASUSessionCard';
import {
  clientMissionsEnabled,
  dashboardCTAButtonsEnabled,
  iraContributionCardEnabled,
  oldBenefitsEnabled,
  rmdGaugeEnabled,
} from 'services/FeatureFlags';
import WithUserPermissions from 'components/shared/WithUserPermissions/WithUserPermissions';
import IRAContributionCard from 'components/layouts/IRAContributionCard/IRAContributionCard';
import IRADataProvider from 'components/shared/DataProviders/IRADataProvider';
import { getExternalInvestmentsBalances } from 'state/external/ExternalInvestmentsBalancesSelectors';
import { getCanViewIRACard } from 'state/view/ViewSelectors';
import { getHasReceivedAccounts } from 'state/requests/RequestStatusSelectors';
import HouseholdInformationCard from 'components/layouts/HouseholdInformationCard/HouseholdInformationCard';
import HouseholdDataProvider from 'components/shared/DataProviders/HouseholdDataProvider';
import ActiveAdvisoryDashboardMain from './ActiveAdvisoryDashboardMain';
import DashboardMain from './DashboardMain';
import {
  isAdviceConsentExpired,
  isAdvicePlanningClient,
  isAdvicePlanningOnboardingEngagementCompleted,
  isRetirementAdvisoryServiceClient
} from 'state/customer/CustomerSelectors';
import ConfidenceScoreCard from 'components/layouts/ConfidenceScoreCard/ConfidenceScoreCard';
import IncompleteRASApplicationCard from 'components/layouts/IncompleteRASApplicationCard/IncompleteRASApplicationCard';
import Toaster from 'components/layouts/Toaster/Toaster';
import { getHasReceivedPrimaryData } from 'state/requests/RequestStatusSelectors';
import { DashboardLoadingSkeleton } from 'components/layouts/LoadingSkeleton/LoadingSkeleton';
import {
  getHasWorkPlaceRetirementAccounts,
  getOwnsCollegeSavings,
  getOwnsCollegeSavingsOnly,
  hasCustodianMaryland529
} from 'state/account/AccountSelectors';
import PrintButton from 'components/shared/PrintButton/PrintButton';
import HeaderIcon from 'components/shared/HeaderIcon/HeaderIcon';
import { ReactComponent as Customize } from 'assets/icons/customize.svg';
import ClientMissions from 'components/shared/ClientMissions/ClientMissions';
import PortfolioAllocationCard from 'components/layouts/PortfolioAllocation/PortfolioAllocationCardWrapper';

const CTAMbox = 'ret-aa-new-dashboard-cta-msg';
const PromoMbox = 'ret-aa-new-dashboard-promo';
const dataAtProperty = 'eb143c91-3999-92fd-60bc-2dca0b84690f';
const CTATarget = TestAndTarget(CallToAction, CTAMbox, dataAtProperty);
const PromoTarget = TestAndTarget(PromotionsCard, PromoMbox, dataAtProperty);
const InsightsTarget = TestAndTarget(InsightCards, PromoMbox, dataAtProperty);
const IRAContributionCardWithData = IRADataProvider(IRAContributionCard);
const HouseholdInformationCardWithData = HouseholdDataProvider(
  HouseholdInformationCard
);

const AggregatePerformanceCardWithData = AggregatePerformanceDataProvider(
  AggregatePerformanceCard
);
const ServiceBenefitWithData = ServiceBenefitsDataProvider(ServiceBenefits);
const RequiredMinimumDistributionCardWithData = rmdGaugeEnabled()
  ? RequiredMinimumDistributionDataProvider(RequiredMinimumDistributionCard)
  : RequiredMinimumDistributionDataProvider(
      CurrentRequiredMinimumDistributionCard
    );
const ExternalInvestmentProductCardWithPermissions = WithUserPermissions(
  ExternalInvestmentProductCard
);
const goTuitionAndEducationPlanningCenterDisclosure = contentConverter.makeHtml(
  Content.goTuitionAndEducationPlanningCenterDisclosure
);
const goTuitionDisclosure = contentConverter.makeHtml(
  Content.goTuitionDisclosure
);
const oldBenefitsCardEnabled = oldBenefitsEnabled();

const DashboardPage = ({
  activeVendorSessions,
  allocations,
  callToAction,
  canViewIRACard,
  hasCollegeSavingsOnly,
  hasOwnedMaryland529Only,
  hasOwnedAlaska529OrNational529,
  hasExternalInvestmentConsent,
  hasManaged,
  hasWorkplaceRetirementAccountsOnly,
  isAdvicePlanningClient,
  isAdvicePlanningOnboardingEngagementCompleted,
  isAdviceConsentExpired,
  isRetirementAdvisoryServiceClient,
  showPersonalizedServices,
  view,
  externalInvestmentBalances,
  externalInvestmentRequestTime,
  promoData,
  thingsToConsider,
  isDesktopLayout,
  hasReceivedAccounts,
  requestStatus,
  hasReceivedDashboardData,
  isCustodian
}) => {
  const isLoading = !hasReceivedDashboardData;
  return (
    <div>
      <DashboardLoadingSkeleton isActive={isLoading} />
      {!isLoading && (
        <Fragment>
          <CTATarget
            messageName={callToAction.messageName}
            messageText={callToAction.messageText}
            messageId={callToAction.messageId}
          />
          <div className="dashboard-page">
            <div className="top-line">
              {!hasWorkplaceRetirementAccountsOnly && (
                <SectionHeader
                  title={
                    <div>
                      <AsOfDateModal
                        label="All data shown as of"
                        containerClassName="dashboard-as-of-date"
                      />
                    </div>
                  }
                />
              )}

              {dashboardCTAButtonsEnabled() && (
                <div className="cta-container">
                  <HeaderIcon
                    to="/profile/customize-account-view"
                    internal={true}
                    icon={Customize}
                    classPrefix="dashboard-page-header"
                    label="Customize"
                  />
                  <PrintButton
                    handlePrint={() => window.print()}
                    isDesktopLayout={isDesktopLayout}
                    className="dashboard-print-button"
                  />
                </div>
              )}
            </div>

            <div className="dashboard-top-group">
              <section className="dashboard-main-section">
                <NASUSessionCard />
                <UnfundedAccountCard />
                {clientMissionsEnabled() && (
                  <ClientMissions showVisualProgress={false} />
                )}
                {isRetirementAdvisoryServiceClient || hasManaged ? (
                  <ActiveAdvisoryDashboardMain view={view} />
                ) : (
                  <DashboardMain view={view} allocations={allocations}  />
                )}

                {!hasCollegeSavingsOnly &&
                  !hasWorkplaceRetirementAccountsOnly && (
                    <ExternalInvestmentProductCardWithPermissions
                      activeVendorSessions={activeVendorSessions}
                      hasConsent={hasExternalInvestmentConsent}
                      balances={externalInvestmentBalances}
                      requestStatus={requestStatus}
                      requestTime={externalInvestmentRequestTime}
                      isDesktopLayout={isDesktopLayout}
                      view={view}
                      shouldMask
                    />
                  )}
              </section>

              <div className="dashboard-page-cards">
                {!(isRetirementAdvisoryServiceClient || hasManaged) && (
                  <AggregatePerformanceCardWithData view={view} />
                )}

                {iraContributionCardEnabled() && canViewIRACard && (
                  <IRAContributionCardWithData view={view} />
                )}

                {hasReceivedAccounts && (
                  <RequiredMinimumDistributionCardWithData view={view} />
                )}

                {hasReceivedAccounts &&
                  !hasCollegeSavingsOnly &&
                  !hasWorkplaceRetirementAccountsOnly && (
                    <PortfolioAllocationCard />
                  )}

                {isAdvicePlanningClient &&
                  !isRetirementAdvisoryServiceClient &&
                  !isAdviceConsentExpired &&
                  !isAdvicePlanningOnboardingEngagementCompleted && (
                    <IncompleteRASApplicationCard />
                  )}

                {isRetirementAdvisoryServiceClient && <ConfidenceScoreCard />}

                <PromoTarget {...promoData} />

                {showPersonalizedServices && hasCollegeSavingsOnly && (
                  <PersonalizedServices view={view} stacked isCustodian={isCustodian}/>
                )}

                {oldBenefitsCardEnabled ? (
                  <ServiceBenefitWithData />
                ) : (
                  <HouseholdInformationCardWithData />
                )}
              </div>
            </div>

            {showPersonalizedServices && !hasCollegeSavingsOnly && (
              <div className="dashboard-personalized-services-plus">
                <PersonalizedServices view={view} isCustodian={isCustodian}/>
              </div>
            )}

            {!isEmpty(thingsToConsider) && (
              <InsightsTarget insights={thingsToConsider} />
            )}

            {hasOwnedAlaska529OrNational529 && (
              <div className="dashboard-legal-copy-container">
                <span
                  className="dashboard-legal-copy-text"
                  dangerouslySetInnerHTML={{
                    __html: goTuitionAndEducationPlanningCenterDisclosure
                  }}
                />
              </div>
            )}

            {hasOwnedMaryland529Only && (
              <div className="dashboard-legal-copy-container">
                <span
                  className="dashboard-legal-copy-text"
                  dangerouslySetInnerHTML={{ __html: goTuitionDisclosure }}
                />
              </div>
            )}
          </div>
          <Toaster />
        </Fragment>
      )}
    </div>
  );
};

DashboardPage.propTypes = {
  allocations: object,
  callToAction: object
};

const mapStateToProps = (state) => {
  const {
    balances,
    externalInvestmentRequestTime,
    hasExternalInvestmentConsent,
    view,
    irtMessages,
    requestStatus
  } = state;

  return {
    allocations: {
      retirement: balances.retirement,
      nonRetirement: balances.nonRetirement
    },
    hasCollegeSavingsOnly: getOwnsCollegeSavingsOnly(state),
    hasOwnedMaryland529Only:
      view.hasOwnedMaryland529 &&
      !(view.hasOwnedAlaska529 || view.hasOwnedNational529),
    hasOwnedAlaska529OrNational529:
      view.hasOwnedAlaska529 || view.hasOwnedNational529,
    hasManaged: view.hasManaged,
    hasWorkplaceRetirementAccountsOnly: view.hasWorkplaceRetirementAccountsOnly,
    hasWorkplaceRetirementAccounts: getHasWorkPlaceRetirementAccounts(state),
    isAdvicePlanningClient: isAdvicePlanningClient(state),
    isAdviceConsentExpired: isAdviceConsentExpired(state),
    isAdvicePlanningOnboardingEngagementCompleted:
      isAdvicePlanningOnboardingEngagementCompleted(state),
    isRetirementAdvisoryServiceClient: isRetirementAdvisoryServiceClient(state),
    showPersonalizedServices: getOwnsCollegeSavings(state),
    canViewIRACard: getCanViewIRACard(state),
    view,
    requestStatus,
    hasExternalInvestmentConsent,
    externalInvestmentBalances: getExternalInvestmentsBalances(state),
    externalInvestmentRequestTime,
    promoData: irtMessages[irtMessageTypes.PROMO],
    callToAction: irtMessages[irtMessageTypes.CALL_TO_ACTION] || {},
    thingsToConsider: irtMessages[irtMessageTypes.CONTENT] || [],
    irtMessages,
    isDesktopLayout: view.isDesktopLayout,
    hasReceivedAccounts: getHasReceivedAccounts(state),
    hasReceivedDashboardData: getHasReceivedPrimaryData(state),
    isCustodian: hasCustodianMaryland529(state)
  };
};

export default withRouter(connect(mapStateToProps)(DashboardPage));
